import { useState } from 'react';
import axios from "axios";
import moment from 'moment';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
} from "@chakra-ui/modal";
import { Button } from '@chakra-ui/react';
import InputField from "components/fields/InputField";
import "react-datepicker/dist/react-datepicker.css";
import { FiDownload } from "react-icons/fi";

interface PayloadType {
    [key: string]: any;
}

const TransactionExportModal: React.FC<{
    setSwalProps: any;
    isOpen: boolean;
    onClose(): void;
    userPermissions: any;
    filters: any;
    dataFor: string;
    isReadyToExport: boolean;
    dateFiltersForExport: any;
    selectedDates: any;
}> = ({
    setSwalProps,
    isOpen,
    onClose,
    userPermissions,
    filters,
    dataFor,
    isReadyToExport,
    dateFiltersForExport,
    selectedDates
}) => {
        const [isProcessing, setIsProcessing] = useState<boolean>(false);
        const [receiverEmail, setReceiverEmail] = useState<string>("");
        const subaccount_id = localStorage.getItem("subaccountId");
        const agent_id = localStorage.getItem("agentId");
        const userRole = localStorage.getItem("userRole")?.toLowerCase();

        const isFormValid = receiverEmail !== "";

        const closeModal = async () => {
            setReceiverEmail("");
            onClose();
        };

        const handleSubmit = async () => {
            try {
                setIsProcessing(true);
                const formattedFromDate = moment(selectedDates[0]).format('YYYY-MM-DD 00:00');
                const formattedToDate = moment(selectedDates[1]).format('YYYY-MM-DD 23:59');

                const merchantId = localStorage.getItem("merchantIdToFilterTransaction");
                const subaccountId = localStorage.getItem("subaccountIdToFilterTransaction");
                const providerId = localStorage.getItem("providerIdToFilterTransaction");
                const providerAccountId = localStorage.getItem("providerAccountIdToFilterTransaction");
                const agentId = localStorage.getItem("agentIdToFilterTransaction");

                const queryObject: PayloadType = {
                    is_export: true,
                    from: formattedFromDate,
                    to: formattedToDate,
                    email: receiverEmail,
                    userRole: userRole,
                    username: localStorage.getItem("username"),
                    ...(userRole !== "agent" && { subaccount_id: subaccount_id }),
                    ...(userRole === "agent" && { reseller_id: agent_id, display_mode: "agent" })
                };

                if (filters?.status !== "" && filters?.status !== undefined) {
                    queryObject.status = filters?.status
                }

                if (filters?.asset !== "" && filters?.asset !== undefined) {
                    queryObject.asset = filters?.asset
                }

                if (filters?.payout_id !== "" && filters?.payout_id !== undefined) {
                    queryObject.payout_id = filters?.payout_id
                }

                if (userRole === 'superadmin') {
                    delete queryObject.subaccount_id;
                    delete queryObject.reseller_id;

                    if (merchantId && merchantId !== "" && merchantId !== undefined) {
                        queryObject.merchant_id = merchantId
                    }

                    if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
                        if (agentId && agentId !== "" && agentId !== undefined && providerId && providerId !== "" && providerId !== undefined) {
                            queryObject.reseller_subaccount_id = subaccountId
                        } else {
                            queryObject.subaccount_id = subaccountId
                        }
                    }

                    if (agentId && agentId !== "" && agentId !== undefined) {
                        queryObject.reseller_id = agentId
                    }

                    if (providerId && providerId !== "" && providerId !== undefined) {
                        queryObject.provider_id = providerId
                    }

                    if (providerId && providerId !== "" && providerId !== undefined && providerAccountId && providerAccountId !== "" && providerAccountId !== undefined) {
                        delete queryObject.provider_id;
                        queryObject.provider_account_id = providerAccountId
                    }

                    if (agentId && agentId !== "" && agentId !== undefined && providerId && providerId !== "" && providerId !== undefined) {
                        queryObject.data_for = dataFor
                    }
                }

                const queryString = Object.keys(queryObject)
                    .map(
                        (key) =>
                            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
                    )
                    .join("&");

                await axios.get(process.env.REACT_APP_API_URL + `/api/transaction/get-all-transactions?${queryString}`)
                    .then(function (response) {
                        if (response && response.data?.status === 'ok') {
                            closeModal();
                            setSwalProps({
                                show: true,
                                icon: "success",
                                title: "Success",
                                html: "Your request has been successfully submitted",
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                    setIsProcessing(false);
                                },
                            });
                        } else {
                            setIsProcessing(false);
                            setSwalProps({
                                show: true,
                                icon: "error",
                                title: "Oops!",
                                html: "Your request could not be submitted successfully, please try again",
                                showConfirmButton: true,
                                didClose: () => {
                                    setSwalProps({});
                                },
                            });
                        }
                    })
                    .catch(function (error) {
                        console.log('error.response: ', error.response);
                        setIsProcessing(false);
                        setSwalProps({
                            show: true,
                            icon: "error",
                            title: "Oops!",
                            html: error.response.data.message,
                            showConfirmButton: true,
                            didClose: () => {
                                setSwalProps({});
                                setIsProcessing(false);
                            },
                        });
                    });
            } catch (error: any) {
                setIsProcessing(false);
                setSwalProps({
                    show: true,
                    icon: "error",
                    title: "Oops!",
                    html: "Something went wrong, please try again later.",
                    showConfirmButton: true,
                    didClose: () => {
                        setSwalProps({});
                        setIsProcessing(false);
                    },
                });
            }
        };

        const getMerchantName = () => {
            return localStorage.getItem("merchantNameToFilterTransaction") || "";
        }

        const getSubaccountName = () => {
            return localStorage.getItem("subaccountNameToFilterTransaction") || "";
        }

        const getAgentName = () => {
            return localStorage.getItem("agentNameToFilterTransaction") || "N/A";
        }

        const getProviderName = () => {
            return localStorage.getItem("providerNameToFilterTransaction") || "N/A";
        }

        const getProviderAccountName = () => {
            return localStorage.getItem("providerAccountNameToFilterTransaction") || "N/A";
        }

        return (
            <>
                <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
                        <ModalBody className="p-6 md:p-7">
                            <div className="mb-4 flex items-start gap-3">
                                <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
                                    <FiDownload size={20} />
                                </div>
                                <div>
                                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        Export Transaction
                                    </h1>
                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                                        {isReadyToExport 
                                            ? "Review your filters and provide an email to receive the export" 
                                            : "Submit your filters before proceeding with export"}
                                    </p>
                                </div>
                            </div>

                            {!isReadyToExport && (
                                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300">
                                    To ensure the exported file reflects your latest criteria, make sure the updated filters are submitted before proceeding with export.
                                </div>
                            )}

                            {isReadyToExport && (
                                <>
                                    <div className="dark:bg-neutral-800/50 mb-6 rounded-xl bg-gray-50 p-4 ring-1 ring-gray-100 dark:ring-white/10">
                                        <dl className="flex flex-col gap-3 text-sm">
                                            <div className="flex">
                                                <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                    Date
                                                </dt>
                                                <dd className="font-medium text-gray-900 dark:text-white">
                                                    {dateFiltersForExport.from + " - " + dateFiltersForExport.to}
                                                </dd>
                                            </div>

                                            {filters.merchant_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Merchant
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {getMerchantName()}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.subaccount_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Subaccount
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {getSubaccountName()}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.payout_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Payout ID
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {filters.payout_id}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.asset !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Asset
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {filters.asset}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.status !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Status
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {filters.status === "complete" ? "completed" : filters.status}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.agent_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Agent
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {getAgentName()}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.provider_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Provider
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {getProviderName()}
                                                    </dd>
                                                </div>
                                            )}

                                            {filters.provider_account_id !== "" && (
                                                <div className="flex">
                                                    <dt className="w-36 shrink-0 text-gray-700 dark:text-gray-400">
                                                        Provider Account
                                                    </dt>
                                                    <dd className="font-medium text-gray-900 dark:text-white">
                                                        {getProviderAccountName()}
                                                    </dd>
                                                </div>
                                            )}
                                        </dl>
                                    </div>

                                    <div className="mb-6">
                                        <InputField
                                            disabled={isProcessing}
                                            variant="auth"
                                            extra="shadow-sm"
                                            label="Please fill in email to receive report"
                                            required={true}
                                            placeholder=""
                                            id="receiver_email"
                                            type="text"
                                            autoComplete="off"
                                            value={receiverEmail}
                                            onChange={(e) => setReceiverEmail(e.target.value)}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex justify-end gap-2">
                                <Button
                                    onClick={() => closeModal()}
                                    isDisabled={isProcessing}
                                    className="btn btn-default"
                                >
                                    Cancel
                                </Button>
                                {isReadyToExport &&
                                    <Button
                                        onClick={() => handleSubmit()}
                                        isDisabled={!isFormValid || isProcessing}
                                        isLoading={isProcessing}
                                        loadingText='Processing...'
                                        className="btn btn-primary"
                                    >
                                        Export
                                    </Button>
                                }
                            </div>
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </>
        );
    };

export default TransactionExportModal;